import { useEffect, useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * Work In Progress page displayed for production domain (guildglory.digital)
 * Shows a coming soon message with links to staging environment and documentation
 */
export default function WorkInProgress() {
  const { t } = useTranslation();
  const [currentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    // Update document title
    document.title = t("workInProgress.pageTitle");
  }, [t]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-8 text-center">
          <div className="text-6xl mb-4">🎲</div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
            {t("workInProgress.title")}
          </h1>
          <p className="text-purple-100 text-lg">
            {t("workInProgress.subtitle")}
          </p>
        </div>

        {/* Content */}
        <div className="p-8 md:p-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {t("workInProgress.heading")}
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              {t("workInProgress.description")}
            </p>
            <div className="inline-block bg-purple-50 border-2 border-purple-200 rounded-lg p-4 mb-8">
              <p className="text-purple-700 font-semibold">
                {t("workInProgress.tryGame")}
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <a
              href="https://guildglory.vercel.app"
              data-testid="WorkInProgress-staging-link"
              className="block bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="text-3xl mb-3">🧪</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {t("workInProgress.stagingTitle")}
              </h3>
              <p className="text-gray-600 mb-3">
                {t("workInProgress.stagingDescription")}
              </p>
              <span className="inline-block bg-amber-500 text-white px-4 py-2 rounded-lg font-semibold text-sm">
                {t("workInProgress.stagingButton")}
              </span>
            </a>

            <a
              href="https://jonatasu.github.io/gg"
              data-testid="WorkInProgress-docs-link"
              className="block bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="text-3xl mb-3">📚</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {t("workInProgress.docsTitle")}
              </h3>
              <p className="text-gray-600 mb-3">
                {t("workInProgress.docsDescription")}
              </p>
              <span className="inline-block bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold text-sm">
                {t("workInProgress.docsButton")}
              </span>
            </a>
          </div>

          {/* Additional Links */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 text-center">
              {t("workInProgress.moreResources")}
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://github.com/jonatasu/gg"
                data-testid="WorkInProgress-github-link"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>💻</span>
                <span className="font-medium text-gray-700">
                  {t("workInProgress.githubRepo")}
                </span>
              </a>
              <a
                href="https://guildglory.vercel.app/api-docs"
                data-testid="WorkInProgress-api-docs-link"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>📖</span>
                <span className="font-medium text-gray-700">
                  {t("workInProgress.apiDocs")}
                </span>
              </a>
            </div>
          </div>

          {/* Tech Stack */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500 mb-3">
              {t("workInProgress.builtWith")}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                "React 19",
                "Vite",
                "Node.js",
                "WebSockets",
                "Tailwind CSS",
                "i18n",
              ].map((tech) => (
                <span
                  key={tech}
                  className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 py-6 text-center border-t border-gray-200">
          <p className="text-gray-600 text-sm">
            {t("workInProgress.footer", { year: currentYear })}
          </p>
          <p className="text-gray-500 text-xs mt-1">
            {t("workInProgress.footerSubtitle")}
          </p>
        </div>
      </div>
    </div>
  );
}
